/**
 * Photographers — Supabase direct CRUD
 * ─────────────────────────────────────────────────────────────
 * Replaces the legacy REST photographersApi for the Supabase path.
 * Schema: photographers table (15 columns, matches Photographer type 1:1).
 * RLS: anon SELECT / INSERT / UPDATE allowed.
 */
import type { Photographer } from "../store/types";
import { getSupabaseClient } from "../lib/supabaseClient";

function db() {
  return getSupabaseClient().from("photographers");
}

function now() {
  return new Date().toISOString();
}

export type PhotographerInsert = Omit<Photographer, "id" | "created_at" | "updated_at">;
export type PhotographerUpdate = Partial<PhotographerInsert>;

/** LIST — returns all photographers ordered by created_at */
export async function listPhotographers(): Promise<Photographer[]> {
  const { data, error } = await db()
    .select("*")
    .order("created_at", { ascending: true });

  if (error) throw new Error(`[photographers] list failed: ${error.message}`);
  return (data ?? []) as Photographer[];
}

/** CREATE — inserts a new photographer row */
export async function createPhotographer(payload: PhotographerInsert): Promise<Photographer> {
  const { data, error } = await db()
    .insert({ ...payload, created_at: now(), updated_at: now() })
    .select()
    .maybeSingle();

  if (error) throw new Error(`[photographers] create failed: ${error.message}`);
  if (!data) throw new Error("[photographers] create returned no data");
  return data as Photographer;
}

/** UPDATE — patches an existing photographer row by UUID */
export async function updatePhotographer(id: string, payload: PhotographerUpdate): Promise<Photographer> {
  const { data, error } = await db()
    .update({ ...payload, updated_at: now() })
    .eq("id", id)
    .select()
    .maybeSingle();

  if (error) throw new Error(`[photographers] update failed: ${error.message}`);
  if (!data) throw new Error(`[photographers] update returned no data for id=${id}`);
  return data as Photographer;
}

/** DEACTIVATE — soft-delete: sets is_active = false */
export async function deactivatePhotographer(id: string): Promise<Photographer> {
  return updatePhotographer(id, { is_active: false, status: "inactive" });
}
